import React from "react";

function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

/** Default range: the current calendar month to date (1st through today) —
 * roughly matches what the old monthly PeriodPicker showed by default. */
export function useDefaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { startDate: toIsoDate(start), endDate: toIsoDate(now) };
}

function parseIsoDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** The period of equal length immediately preceding `range`, e.g. for
 * Aug 1–20 (20 days) this returns Jul 12–31 (the 20 days right before Aug 1).
 * Used as the default "compare to" range on the "By Service" page — a
 * period-over-period comparison is the most common thing to reach for. */
export function previousPeriod(range) {
  const start = parseIsoDate(range.startDate);
  const end = parseIsoDate(range.endDate);
  const lengthDays = Math.round((end - start) / ONE_DAY_MS) + 1;
  const newEnd = new Date(start.getTime() - ONE_DAY_MS);
  const newStart = new Date(newEnd.getTime() - (lengthDays - 1) * ONE_DAY_MS);
  return { startDate: toIsoDate(newStart), endDate: toIsoDate(newEnd) };
}

/** Two plain HTML date inputs for picking an arbitrary, inclusive start/end
 * date range. `value` is { startDate, endDate } (both 'YYYY-MM-DD').
 * `onChange` receives the updated { startDate, endDate } object. `endDate`
 * is clamped to today (Cost Explorer has no data for the future), and
 * `startDate` can't be set after the current `endDate` (browser `max`
 * attribute keeps this from happening interactively; the small correction
 * below covers the case where the user picks a start date that got saved
 * before this was in place, or if `value` was set programmatically). */
export default function DateRangePicker({ value, onChange }) {
  const today = toIsoDate(new Date());
  const { startDate, endDate } = value;

  const handleStart = (e) => {
    const next = e.target.value;
    onChange({ startDate: next, endDate: endDate < next ? next : endDate });
  };

  const handleEnd = (e) => {
    const next = e.target.value;
    onChange({ startDate: startDate > next ? next : startDate, endDate: next });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="date" value={startDate} max={today} onChange={handleStart} aria-label="Start date" />
      <span className="muted" style={{ fontSize: 13 }}>
        to
      </span>
      <input type="date" value={endDate} max={today} onChange={handleEnd} aria-label="End date" />
    </div>
  );
}
